import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { capitalize } from "lodash";
import { useFragment } from "react-relay";

import { formatSliderTimeLabel } from "@/components/AgendaFilters/utils/getSliderOptions";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { durationDisplayFromMinutesShort } from "../../../../../utils/formatTime";
import { agendaQueryInputsAtom } from "../../../atoms/agendaQueryInputs";
import { useGetDurationBasedAgendaFilterBarValues_facility$key } from "./__generated__/useGetDurationBasedAgendaFilterBarValues_facility.graphql";

export const useGetDurationBasedAgendaFilterBarValues = (
  facilityRef: useGetDurationBasedAgendaFilterBarValues_facility$key | null
) => {
  const agendaQueryInputs = useAtomValue(agendaQueryInputsAtom);
  const facility = useFragment(
    useGetDurationBasedAgendaFilterBarValuesFragmentString,
    facilityRef
  );

  const courseOverride = facility?.copyOverrides?.course;
  const venueLabel = getNounOverride(courseOverride?.singular, t`Course`);
  const venueLabelPlural = getNounOverride(courseOverride?.plural, t`Courses`);

  const startTime = facility?.startEndTime?.startTime;
  const endTime = facility?.startEndTime?.endTime;

  const facilityCourseBuckets =
    facility?.courseBuckets?.edges?.flatMap((e) => (e?.node ? [e.node] : [])) ||
    [];

  const courseBucketDisplay = (
    courseBucketIds: string[] | null
  ): string | null => {
    if (!courseBucketIds) {
      return null;
    }
    return courseBucketIds.length > 1
      ? `${courseBucketIds.length} ${venueLabelPlural}`
      : facilityCourseBuckets?.find(
          (bucket) => bucket.id === courseBucketIds[0]
        )?.name ?? "";
  };

  const timeOfDayDisplay = (
    timeOfDay: { timeGTE?: string; timeLTE?: string } | null
  ): string | null => {
    if (timeOfDay === null) {
      return null;
    }

    const formattedStartTime = formatSliderTimeLabel(
      timeOfDay.timeGTE || startTime || ""
    );
    const formattedEndTime = formatSliderTimeLabel(
      timeOfDay.timeLTE || endTime || ""
    );

    return formattedStartTime && formattedEndTime
      ? `${formattedStartTime} - ${formattedEndTime}`
      : null;
  };

  const agendaFilterBarValues = [
    {
      value: durationDisplayFromMinutesShort(
        agendaQueryInputs.duration ?? null
      ),
      placeholder: t`Duration`,
    },
    {
      value: groupSizeDisplay(agendaQueryInputs.groupSize ?? null),
      placeholder: t`No. of people`,
    },
    {
      value: timeOfDayDisplay(agendaQueryInputs.times ?? null),
      placeholder: t`Time of day`,
    },
    {
      value: courseBucketDisplay(agendaQueryInputs.courseBucketIds ?? null),
      placeholder: `${capitalize(venueLabel)} ${t`type`}`,
    },
  ];

  return agendaFilterBarValues;
};

const groupSizeDisplay = (groupSize: number | null): string | null => {
  if (groupSize === null) {
    return null;
  }
  return groupSize === 1 ? "1 person" : `${groupSize} people`;
};

const useGetDurationBasedAgendaFilterBarValuesFragmentString = graphql`
  fragment useGetDurationBasedAgendaFilterBarValues_facility on Facility {
    startEndTime {
      startTime
      endTime
    }
    courseBuckets {
      edges {
        node {
          id
          name
        }
      }
    }
    copyOverrides {
      course {
        singular
        plural
      }
    }
  }
`;
